'use client';

import { EventThemeApi } from '@/lib/pre-registration-api';
import { ShopStyle } from '@/lib/TicketApi';
import React, { useMemo, useRef } from 'react';
import { StyleOverwritesContext } from './StyleOverwrites';

interface ThemeStyleOverwritesProps {
    children: JSX.Element | React.ReactNode;
    theme: EventThemeApi;
}

/** Compare two theme colors as authored, ignoring case and stray whitespace. */
function sameColor(a: string, b: string): boolean {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Derive the legacy ShopStyle shape from an event theme so that context
 * consumers (useShopStyle) keep working unchanged.
 */
export function shopStyleFromTheme(theme: EventThemeApi): ShopStyle {
    /*
     * Themes are authored for the visitor app, which renders primary buttons on the
     * page `background`. This page renders them inside a card, so a theme that sets
     * `primary` to the same color as `card` leaves the submit button invisible —
     * fill and surround are one flat block, with only the label to hint at a button.
     * Outdoor Stereo (card, primary and primaryBorder all #ee4287) does exactly this,
     * so a border cannot rescue it either.
     *
     * When they collide, fall back to the theme's other designed surface pair,
     * (background, foreground) — the organizer picked those to sit next to `card`,
     * so the result reads as intentional rather than as an arbitrary accent. A theme
     * whose background also matches its card is degenerate and left alone.
     */
    const ctaCollidesWithCard = sameColor(theme.primary, theme.card);
    const ctaButtonColor = ctaCollidesWithCard ? theme.background : theme.primary;
    const ctaButtonTextColor = ctaCollidesWithCard ? theme.foreground : theme.primaryForeground;

    return {
        panelBackgroundColor: theme.card,
        textColor: theme.cardForeground,
        borderColor: theme.cardBorder || theme.border,
        addToCartButtonColor: theme.primary,
        addToCartButtonTextColor: theme.primaryForeground,
        removeFromCartButtonColor: theme.primary,
        removeFromCartButtonTextColor: theme.primaryForeground,
        ctaButtonColor,
        ctaButtonTextColor,
        productErrorMessageBackground: theme.destructive,
        productErrorMessageText: theme.destructiveForeground,
        // Flat page background: both gradient ends the same color.
        pageBackgroundFromColor: theme.background,
        pageBackgroundToColor: theme.background,
        pageBackgroundImage: theme.backgroundImage?.url,
    };
}

/**
 * Same wrapper as StyleOverwrites, but the `--shop-*` CSS custom properties
 * are derived from an event theme instead of a ShopStyle. It provides the
 * same StyleOverwritesContext, so shared children (Header, MainButton,
 * CustomerDetailsForm, Dialog, ...) work without changes.
 */
export function ThemeStyleOverwrites({ children, theme }: ThemeStyleOverwritesProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const shopStyle = useMemo(() => shopStyleFromTheme(theme), [theme]);

    const styleOverrides = useMemo(() => ({
        "--shop-panelBackgroundColor": shopStyle.panelBackgroundColor,
        "--shop-textColor": shopStyle.textColor,
        "--shop-removeFromCartButtonColor": shopStyle.removeFromCartButtonColor,
        "--shop-removeFromCartButtonTextColor": shopStyle.removeFromCartButtonTextColor,
        "--shop-addToCartButtonColor": shopStyle.addToCartButtonColor,
        "--shop-addToCartButtonTextColor": shopStyle.addToCartButtonTextColor,
        "--shop-productErrorMessageBackground": shopStyle.productErrorMessageBackground,
        "--shop-productErrorMessageText": shopStyle.productErrorMessageText,
        "--shop-ctaButtonColor": shopStyle.ctaButtonColor,
        "--shop-ctaButtonTextColor": shopStyle.ctaButtonTextColor,
        "--shop-borderColor": shopStyle.borderColor,
        "--shop-pageBackgroundFromColor": shopStyle.pageBackgroundFromColor,
        "--shop-pageBackgroundToColor": shopStyle.pageBackgroundToColor,
    } as React.CSSProperties), [shopStyle]);

    return (
        <StyleOverwritesContext.Provider value={{ containerRef, shopStyle }}>
            <div ref={containerRef} style={styleOverrides}>
                {children}
            </div>
        </StyleOverwritesContext.Provider>
    );
}
